import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {Plus, Edit, Trash2, Search, Filter, Star, Eye, EyeOff} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface Product {
  _id: string
  name: string
  nameEn?: string
  slug: string
  category: string
  price: number
  originalPrice?: number
  costPrice?: number
  description: string
  descriptionEn?: string
  images: string[]
  origin?: string
  region?: string
  brewingMethods?: string[]
  flavorNotes?: string[]
  specifications?: {
    roast?: string
    variety?: string
    altitude?: string
    processing?: string
    tastingNotes?: string
    weight?: string
  }
  inventory: number
  status: 'active' | 'inactive' | 'draft'
  isFeatured: boolean
  seoTitle?: string
  seoDescription?: string
  createdAt: string
  updatedAt: string
}

const ProductManager: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [categories, setCategories] = useState<{ value: string; label: string }[]>([])

  useEffect(() => {
    fetchCategories()
    fetchProducts()
  }, [])

  const fetchCategories = async () => {
    try {
      const response = await lumi.entities.product_categories.list({
        filter: { status: 'active' },
        sort: { sortOrder: 1 }
      })
      const categoryList = (response.list || []).map((cat: any) => ({
        value: cat.value,
        label: cat.label
      }))
      setCategories(categoryList)
    } catch (error) {
      console.error('Failed to fetch categories:', error)
    }
  }

  const fetchProducts = async () => {
    setLoading(true)
    try {
      const response = await lumi.entities.products.list({
        sort: { createdAt: -1 }
      })
      setProducts(response.list || [])
    } catch (error) {
      console.error('Failed to fetch products:', error)
      toast.error('載入商品失敗')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (productId: string) => {
    if (!confirm('確定要刪除這個商品嗎？')) return

    try {
      await lumi.entities.products.delete(productId)
      toast.success('商品刪除成功')
      fetchProducts()
    } catch (error) {
      console.error('Delete failed:', error)
      toast.error('商品刪除失敗')
    }
  }

  const toggleFeatured = async (product: Product) => {
    try {
      await lumi.entities.products.update(product._id, {
        isFeatured: !product.isFeatured,
        updatedAt: new Date().toISOString()
      })
      toast.success(`已${!product.isFeatured ? '設為' : '取消'}精選商品`)
      fetchProducts()
    } catch (error) {
      console.error('Toggle featured failed:', error)
      toast.error('操作失敗')
    }
  }

  const toggleStatus = async (product: Product) => {
    const newStatus = product.status === 'active' ? 'inactive' : 'active'
    try {
      await lumi.entities.products.update(product._id, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      })
      toast.success(`商品已${newStatus === 'active' ? '啟用' : '停用'}`)
      fetchProducts()
    } catch (error) {
      console.error('Toggle status failed:', error)
      toast.error('操作失敗')
    }
  }

  // 過濾商品
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (product.nameEn?.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory
    const matchesStatus = filterStatus === 'all' || product.status === filterStatus
    
    return matchesSearch && matchesCategory && matchesStatus
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-light tracking-wider mb-2">商品管理</h1>
            <p className="text-gray-400">管理所有商品資訊與庫存</p>
          </div>
          <Link
            to="/admin/products/new"
            className="flex items-center px-6 py-3 bg-gray-400 text-black hover:bg-gray-300 transition-colors duration-200 text-sm font-light tracking-wider"
          >
            <Plus size={16} className="mr-2" />
            新增商品
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8 p-4 bg-gray-950 border border-gray-800">
          <div className="flex items-center space-x-2">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              placeholder="搜尋商品..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent border border-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-gray-500 focus:outline-none"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter size={16} className="text-gray-400" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-gray-100 focus:border-gray-500 focus:outline-none"
            >
              <option value="all">所有分類</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-gray-100 focus:border-gray-500 focus:outline-none"
          >
            <option value="all">所有狀態</option>
            <option value="active">啟用</option>
            <option value="inactive">停用</option>
            <option value="draft">草稿</option>
          </select>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <motion.div
              key={product._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-950 border border-gray-800 overflow-hidden group"
            >
              {/* Product Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={product.images[0] || 'https://images.pexels.com/photos/894695/pexels-photo-894695.jpeg'}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                
                {/* Status Badge */}
                <div className="absolute top-2 left-2">
                  <span className={`px-2 py-1 text-xs font-light tracking-wider ${
                    product.status === 'active' ? 'bg-green-600 text-white' :
                    product.status === 'inactive' ? 'bg-red-600 text-white' :
                    'bg-yellow-600 text-black'
                  }`}>
                    {product.status === 'active' ? '啟用' : product.status === 'inactive' ? '停用' : '草稿'}
                  </span>
                </div>

                {/* Featured Badge */}
                {product.isFeatured && (
                  <div className="absolute top-2 right-2">
                    <Star size={16} className="text-yellow-400 fill-current" />
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-sm font-light text-gray-100 tracking-wide line-clamp-1">
                    {product.name}
                  </h3>
                  <span className="text-xs text-gray-500 ml-2">
                    {categories.find(cat => cat.value === product.category)?.label}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">售價</span>
                    <span className="text-gray-100">NT$ {product.price.toLocaleString()}</span>
                  </div>
                  {product.originalPrice && product.originalPrice > product.price && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">原價</span>
                      <span className="text-gray-500 line-through">NT$ {product.originalPrice.toLocaleString()}</span>
                    </div>
                  )}
                  {product.costPrice && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">成本</span>
                      <span className="text-gray-300">NT$ {product.costPrice.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">庫存</span>
                    <span className={`${product.inventory < 10 ? 'text-red-400' : 'text-gray-100'}`}>
                      {product.inventory}
                    </span>
                  </div>
                </div>

                {/* Bean Specifications (for coffee beans) */}
                {product.category === 'beans' && (
                  <div className="mb-4 p-3 bg-gray-900 border border-gray-800">
                    <div className="text-xs text-gray-400 mb-2">豆單資訊</div>
                    <div className="space-y-1 text-xs">
                      {product.specifications?.roast && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">ROAST</span>
                          <span className="text-gray-300">{product.specifications.roast}</span>
                        </div>
                      )}
                      {product.origin && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">ORIGIN</span>
                          <span className="text-gray-300">{product.origin}</span>
                        </div>
                      )}
                      {product.flavorNotes && product.flavorNotes.length > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">NOTES</span>
                          <span className="text-gray-300 text-right max-w-24 truncate">
                            {product.flavorNotes.join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-between items-center">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => toggleStatus(product)}
                      className="p-1 text-gray-400 hover:text-gray-100 transition-colors"
                      title={product.status === 'active' ? '停用商品' : '啟用商品'}
                    >
                      {product.status === 'active' ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                    <button
                      onClick={() => toggleFeatured(product)}
                      className={`p-1 transition-colors ${
                        product.isFeatured ? 'text-yellow-400' : 'text-gray-400 hover:text-yellow-400'
                      }`}
                      title={product.isFeatured ? '取消精選' : '設為精選'}
                    >
                      <Star size={14} className={product.isFeatured ? 'fill-current' : ''} />
                    </button>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Link
                      to={`/admin/products/edit/${product._id}`}
                      className="p-1 text-gray-400 hover:text-blue-400 transition-colors"
                      title="編輯商品"
                    >
                      <Edit size={14} />
                    </Link>
                    <button
                      onClick={() => handleDelete(product._id)}
                      className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                      title="刪除商品"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">沒有找到符合條件的商品</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductManager